import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import { MatDialogRef, MatDialogModule, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';

import { UserService } from 'app/services/user.service';
import { profileTypeStrategies, UserModel, UserProfileType, PROFILES_WITH_TECHNICAL_DATA, isNurseLikeProfile } from 'app/models/user.model';
import { DoctorService } from 'app/services/doctor.service';
import { DoctorCrmService } from 'app/services/doctor-crm.service';
import { DoctorModel } from 'app/models/doctor.model';
import { DoctorCrmModel } from 'app/models/doctor-crm.model';
import { DoctorCrmDialogComponent } from './dialogs/doctor-crm-dialog/doctor-crm-dialog.component';
import { debounceTime, distinctUntilChanged, finalize, take, takeUntil, filter, firstValueFrom } from 'rxjs';
import { MatSelectModule } from '@angular/material/select';
import { PermissionGroupModel } from 'app/models/permission-group.model';
import { PermissionGroupService } from 'app/services/permission-group.service';
import { RegimeModel } from 'app/models/schedule.model';
import { ScheduleService } from 'app/services/schedule.service';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';
import { NgxCurrencyDirective } from 'ngx-currency';
import { SYSTEM_ALERT_TEMPLATES } from 'app/shared/constants/system-alert-templates';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CompanyService } from 'app/services/company.service';
import { CepService } from 'app/shared/services/cep.service';
import { BaseUnsubscribeComponent } from 'app/core/components/base-unsubscribe.component';
import { DevstroupeConfirmationService } from '@devstroupe/services/confirmation';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HealthPlanService } from 'app/services/health_plan.service';
import { HealthPlanModel } from 'app/models/health_plan.model';
import { MatTabsModule } from '@angular/material/tabs';
import { CFMService } from 'app/shared/services/cfm.service';
import { UserCompanyService } from 'app/services/user_company.service';
import { AutoTranslatePipe } from 'app/shared/pipes/translate.pipe';
import { AutoTranslateDirective } from 'app/shared/directives/auto-translate.directive';
import { UserCompanyModel } from 'app/models/user_company.model';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { PesquisaDialogComponent } from 'app/shared/dialogs/pesquisa-dialog/pesquisa-dialog.component';
import { IFilterPagination } from 'app/shared/interfaces/filter-interface';
import { CompanyModel } from 'app/models/company.model';
import { ApiService } from 'app/shared/apis/api.service';
import { MatDialog } from '@angular/material/dialog';
import { PermissionService } from 'app/core/services/permission.service';
import { HasPermissionDirective } from 'app/shared/directives/has-permission.directive';
import { PhoneFieldComponent } from 'app/shared/components/phone-field/phone-field.component';
import { DEFAULT_COUNTRY_CODE } from 'app/shared/constants/country-codes';
import { ErrorDialogService } from 'app/shared/services/error-dialog.service';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { IntegrationService } from 'app/services/integration.service';
import { AVAILABLE_LANGUAGES, LanguageOption, LanguageService } from 'app/services/language.service';
import { IntegrationModel } from 'app/models/integration.model';
import { normalizeCountryCode } from 'app/shared/utils/vehicle-plate-country';
import { RecordIdBadgeComponent } from 'app/shared/components/record-id-badge/record-id-badge.component';

/** Chaves de aba na ordem lógica do template (deve coincidir com *ngIf dos mat-tab). */
type UserFormTabKey = 'general' | 'crm' | 'technical' | 'attachments' | 'other' | 'companies' | 'notifications';

const USER_FORM_TECHNICAL_FIELD_KEYS = new Set<string>([
    'professional_number',
    'issuing_body',
    'license_issue_date',
    'license_expiry_date',
    'license_issue_date_emission',
    'issuer_uf',
    'registration',
    'license_status',
    'other_registration',
    'specialties_or_courses',
    'practice_area_or_courses',
    'has_additional_courses',
    'additional_courses_details',
    'graduation_institution',
    'graduation_year',
    'approved_status',
]);

const USER_FORM_ATTACHMENT_FIELD_KEYS = new Set<string>(['photo', 'document_card']);

const USER_FORM_OTHER_INFO_FIELD_KEYS = new Set<string>([
    'previous_aph_experience',
    'telemedicine_experience',
    'shift_time_preferences',
    'shift_day_preferences',
    'shift_duration_preferences',
    'average_hourly_rate',
]);

/** Rótulos PT para mensagem de validação (chave do controle). */
const USER_FORM_FIELD_LABELS: Record<string, string> = {
    name: 'Nome',
    type_doc: 'Tipo de Documento',
    num_doc: 'Número do Documento',
    email: 'E-mail',
    password: 'Senha',
    number_phone: 'Telefone',
    address: 'Endereço',
    cep: 'CEP',
    city: 'Cidade',
    number: 'Número',
    complement: 'Complemento',
    sex: 'Sexo',
    birthday_date: 'Data de Nascimento',
    permission_group_id: 'Grupo de Permissão',
    regime_id: 'Regime de escala',
    company_id: 'Empresa',
    profile_type: 'Tipo de perfil',
    health_plan_id: 'Plano',
    plan_number: 'N. Plano',
    professional_number: 'Registro profissional (CRM/COREN/CNH)',
    issuing_body: 'Órgão emissor',
    license_issue_date: 'Data de inscrição / validade',
    license_expiry_date: 'Data (validade / 1ª inscrição UF)',
    license_issue_date_emission: 'Data de emissão',
    issuer_uf: 'UF / Categoria',
    registration: 'Inscrição',
    license_status: 'Situação',
    other_registration: 'Inscrições em outro estado',
    specialties_or_courses: 'Especialidades',
    practice_area_or_courses: 'Área de atuação',
    graduation_institution: 'Instituição de graduação',
    graduation_year: 'Ano de formatura',
    photo: 'Foto',
    document_card: 'Documento / carteira',
    previous_aph_experience: 'Experiência APH',
    telemedicine_experience: 'Telemedicina',
    shift_time_preferences: 'Preferências de horários',
    shift_day_preferences: 'Preferências de dias',
    shift_duration_preferences: 'Preferências de plantões',
    average_hourly_rate: 'Valor médio da hora',
    preferred_ui_language: 'Idioma padrão da interface',
};

@Component({
    selector: 'app-user-form',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        RouterModule,
        MatIconModule,
        MatSelectModule,
        MatDatepickerModule,
        MatProgressSpinnerModule,
        NgxMaskDirective,
        NgxCurrencyDirective,
        MatCheckboxModule,
        MatTooltipModule,
        MatTabsModule,
        MatTableModule,
        MatSlideToggleModule,
        AutoTranslatePipe,
        AutoTranslateDirective,
        HasPermissionDirective,
        PhoneFieldComponent,
        RecordIdBadgeComponent,
    ],
    templateUrl: './user-form.component.html',
    styleUrls: ['./user-form.component.scss'],
})
export class UserFormComponent extends BaseUnsubscribeComponent implements OnInit {
    get currencyMaskOptions() {
        return this.languageService.getNgxCurrencyMaskOptions();
    }

    readonly PASSWORD_SENTINEL = '__PASSWORD_UNCHANGED__';
    readonly DOC_UNCHANGED_SENTINEL = '__DOC_UNCHANGED__';
    sendingResetPassword = false;
    availableUiLanguages: LanguageOption[] = AVAILABLE_LANGUAGES;
    form: FormGroup = this.fb.group({
        user_id: [null],
        name: ['', Validators.required],
        type_doc: ['CPF', Validators.required],
        num_doc: ['', Validators.required],
        email: [''], // Email opcional
        password: [''], // Senha será validada condicionalmente baseada em SYSTEM_ACCESS
        number_phone: [''],
        number_phone_country_code: ['+55'],
        number_phone_is_whatsapp: [0],
        address: [''],
        city: [''],
        cep: [''],
        number: [''],
        complement: [''],
        permission_group_id: [null],
        regime_id: [null],
        sex: [null],
        birthday_date: [null],
        age: [{ value: null, disabled: true }],
        is_admin: [null],
        plan_number: [null],
        company_id: [null],
        status: [true],
        is_available: [true],
        is_regulator: [false],
        profile_type: ['', Validators.required],
        health_plan_id: [null],
        origin: [{ value: 'PAINEL', disabled: true }],

        // Technical Data Fields
        professional_number: [''], // CRM, COREN, CNH
        license_issue_date: [null], // Data de Inscrição/1ª Habilitação
        license_expiry_date: [null], // Data de Validade/Primeira Inscrição na UF
        license_issue_date_emission: [null], // Data de Emissão
        issuer_uf: [''], // UF ou Categoria
        registration: [''], // Inscrição
        license_status: [''], // Situação
        other_registration: [''], // Inscrições em Outro Estado
        specialties_or_courses: [''], // Especialidades
        practice_area_or_courses: [''], // Área de Atuação
        has_additional_courses: [false], // Possui Cursos/CVE
        additional_courses_details: [''], // Detalhes dos cursos
        graduation_institution: [''], // Instituição de Graduação
        graduation_year: [''], // Ano de Formatura

        // Notificações WhatsApp por tipo de alerta
        notificationPreferences: this.fb.group(
            Object.fromEntries(Object.keys(SYSTEM_ALERT_TEMPLATES).map((alertType) => [alertType, [false]])),
        ),

        // Attachment Fields
        photo: [''], // Foto
        document_card: [''], // Documento/Carteira
        approved_status: [false], // Status Aprovado
        rg: [''], // RG
        issuing_body: [''], // Órgão Emissor

        // Outras Informações
        previous_aph_experience: [false], // Experiência prévia com APH
        telemedicine_experience: [false], // Já atuou com Telemedicina
        shift_time_preferences: [[]], // Preferências de horários
        shift_day_preferences: [[]], // Preferências de dias da semana
        shift_duration_preferences: [[]], // Preferências de plantões
        average_hourly_rate: [null], // Valor médio da hora atual
        preferred_ui_language: [null as string | null],
    });

    loading: boolean = false;
    loadingData: boolean = false;
    /** Índice da aba ativa (formulários com mat-tab-group, exceto PACIENTE). */
    selectedTabIndex = 0;
    /** Abas com erro após tentativa de salvar com formulário inválido. */
    invalidTabSections = new Set<UserFormTabKey>();
    config: {
        type: UserProfileType;
        isProfileEdit?: boolean;
    };
    /**
     * company_kind do contexto (acordeão Solicitantes/Parceiros/etc).
     * Persistido no constructora — getUser() não pode apagar isso ao trocar this.data.
     */
    private contextCompanyKind: string | null = null;
    /** company_id do contexto (empresa do acordeão), usado para filtrar grupos. */
    private contextCompanyId: number | null = null;
    permissionGroups: PermissionGroupModel[] = [];
    plans: HealthPlanModel[] = [];
    /** Regimes de escala ativos da empresa (para perfis operacionais). */
    regimes: RegimeModel[] = [];
    loadingRegimes = false;

    // Upload properties
    photoUrl: string = '';
    documentUrl: string = '';

    // User Companies properties
    userCompanies: UserCompanyModel[] = [];
    loadingCompanies: boolean = false;
    companiesColumns: string[] = ['company_name', 'createdAt', 'actions'];
    companiesDataSource = new MatTableDataSource<UserCompanyModel>([]);
    currentUser: UserModel | null = null;

    // Doctor properties
    doctor: DoctorModel | null = null;
    doctorCrms: DoctorCrmModel[] = [];
    loadingDoctor: boolean = false;
    loadingCrms: boolean = false;
    crmsColumns: string[] = ['crm', 'uf', 'status', 'specialties', 'createdAt'];
    crmsDataSource = new MatTableDataSource<DoctorCrmModel>([]);

    // Opções para Outras Informações
    shiftTimeOptions = [
        { value: 'MANHÃ', label: 'Manhã' },
        { value: 'TARDE', label: 'Tarde' },
        { value: 'NOITE', label: 'Noite' },
        { value: 'MADRUGADA', label: 'Madrugada' },
    ];

    shiftDayOptions = [
        { value: 'SEG', label: 'Segunda-feira' },
        { value: 'TER', label: 'Terça-feira' },
        { value: 'QUA', label: 'Quarta-feira' },
        { value: 'QUI', label: 'Quinta-feira' },
        { value: 'SEX', label: 'Sexta-feira' },
        { value: 'SAB', label: 'Sábado' },
        { value: 'DOM', label: 'Domingo' },
    ];

    shiftDurationOptions = [
        { value: 'POR_HORA', label: 'Por hora' },
        { value: '12HRS', label: '12 horas' },
        { value: '24HRS', label: '24 horas' },
        { value: '48HRS', label: '48 horas' },
    ];

    get isAdmin(): boolean {
        return this.currentUser?.is_admin || false;
    }

    /** Documento completo visível/editável (admins globais). */
    get canViewFullDocument(): boolean {
        return this.isAdmin;
    }

    /** Máscara exibida no campo Número do Documento quando o usuário logado não é admin. */
    get maskedDocDisplay(): string {
        const typeDoc = this.form?.controls['type_doc']?.value;
        return typeDoc === 'CNPJ' ? '**.***.***/****-**' : '***.***.***-**';
    }

    /** Regime de escala só faz sentido para perfis que entram em turno. */
    get showRegimeSelect(): boolean {
        return ['DOCTOR', 'NURSE', 'NURSING_TECH', 'DRIVER', 'PARAMEDIC'].includes(this.config.type);
    }

    get showCompaniesTab(): boolean {
        return this.isAdmin && ['DOCTOR', 'NURSE', 'NURSING_TECH', 'DRIVER', 'PARAMEDIC', 'COMPANY_ADMIN'].includes(this.config.type);
    }

    /** Paciente sempre nasce vinculado a uma empresa; admin sem empresa na sidenav precisa escolher manualmente aqui. */
    get showPatientCompanySelect(): boolean {
        return this.isAdmin && this.config.type === 'PATIENT';
    }

    patientCompanies: CompanyModel[] = [];

    // Notificações WhatsApp
    whatsAppIntegrations: IntegrationModel[] = [];
    loadingIntegrations: boolean = false;

    private readonly ALERT_TYPE_DESCRIPTIONS: Record<string, string> = {
        CAMERA_ON: 'Câmera do veículo ficou online.',
        CAMERA_OFF: 'Câmera do veículo ficou offline.',
        VEHICLE_MOVING: 'Veículo entrou em movimento.',
        VEHICLE_STOPPED: 'Veículo parou (velocidade zero ou abaixo do limiar).',
        SERVICE_OPENED: 'Nova ocorrência aberta no sistema.',
        VITALS_MEASURING: 'Monitor de sinais vitais iniciou medição HL7.',
        SERVICE_PENDING_CLOSURE: 'Enviado a cada 1h enquanto a ocorrência estiver aberta sem encerramento.',
        SERVICE_CLOSED: 'Ocorrência finalizada com data/hora de encerramento.',
        EQUIPMENT_DOWN: 'Câmera ou monitor de sinais vitais ficou inoperante.',
        EQUIPMENT_RESTORED: 'Câmera ou monitor de sinais vitais voltou a funcionar.',
        SHIFT_OPENED: 'Turno local (equipe/viatura) foi aberto.',
    };

    readonly SYSTEM_ALERT_TYPES: { type: string; alias: string; description: string }[] = Object.entries(SYSTEM_ALERT_TEMPLATES)
        .filter(([, def]) => !def.deprecated)
        .map(([type, def]) => ({
            type,
            alias: def.alias,
            description: this.ALERT_TYPE_DESCRIPTIONS[type] || def.alias,
        }));

    get eligibleAlertTypes(): { type: string; alias: string; description: string }[] {
        if (!this.whatsAppIntegrations.length) return [];
        const userRole = this.config.type;
        return this.SYSTEM_ALERT_TYPES.filter((a) =>
            this.whatsAppIntegrations.some((integration) => {
                const roleMap = integration.config?.recipientRoles?.[a.type];
                if (!roleMap || typeof roleMap !== 'object') {
                    return userRole === 'ADMIN' || userRole === 'COMPANY_ADMIN';
                }
                return roleMap[userRole] === true;
            }),
        );
    }

    get showNotificationsTab(): boolean {
        return this.config.type !== 'PATIENT';
    }

    /**
     * Na edição: sem prefs persistidas, o backend trata como receber todos os alertas (opt-out só com false explícito).
     * Mescla com true por tipo conhecido e sobrescreve com o que vier do usuário.
     */
    private mergeStoredNotificationPreferences(stored: Record<string, boolean> | null | undefined): void {
        const group = this.form.get('notificationPreferences');
        if (!group) return;
        const defaultsTrue = Object.fromEntries(this.SYSTEM_ALERT_TYPES.map((a) => [a.type, true])) as Record<string, boolean>;
        const merged = {
            ...defaultsTrue,
            ...(stored && typeof stored === 'object' ? stored : {}),
        };
        group.patchValue(merged);
    }

    getNotificationPrefControl(alertType: string) {
        return this.form.get(['notificationPreferences', alertType]);
    }

    // Órgãos Emissores
    issuingBodyOptions = [
        { value: 'ABNC', label: 'ABNC - Academia Brasileira de Neurocirurgia' },
        { value: 'AGU', label: 'AGU - Advocacia-Geral da União' },
        { value: 'ANAC', label: 'ANAC - Agência Nacional de Aviação Civil' },
        { value: 'CAER', label: 'CAER - Clube de Aeronáutica' },
        { value: 'CAU', label: 'CAU - Conselho de Arquitetura e Urbanismo' },
        { value: 'CBM', label: 'CBM - Corpo de Bombeiro Militar' },
        { value: 'CFA', label: 'CFA - Conselho Federal Administração' },
        { value: 'CFB', label: 'CFB - Conselho Federal de Biblioteconomia' },
        { value: 'CFBIO', label: 'CFBIO - Conselho Federal de Biologia' },
        { value: 'CFBM', label: 'CFBM - Conselho Federal de Biomedicina' },
        { value: 'CFC', label: 'CFC - Conselho Federal de Contabilidade' },
        { value: 'CFESS', label: 'CFESS - Conselho Federal de Serviço Social' },
        { value: 'CFF', label: 'CFF - Conselho Regional de Farmácia' },
        { value: 'CFFA', label: 'CFFA - Conselho Federal de Fonoaudiologia' },
        { value: 'CFM', label: 'CFM - Conselho Federal de Medicina' },
        { value: 'CFMV', label: 'CFMV - Conselho Federal de Medicina Veterinária' },
        { value: 'CFN', label: 'CFN - Conselho Federal de Nutrição' },
        { value: 'CFO', label: 'CFO - Conselho Federal de Odontologia' },
        { value: 'CFP', label: 'CFP - Conselho Federal de Psicologia' },
        { value: 'CFQ', label: 'CFQ - Conselho Regional de Química' },
        { value: 'CFT', label: 'CFT - Conselho Federal dos Técnicos Industriais' },
        { value: 'CFTA', label: 'CFTA - Conselho Federal dos Técnicos Agrícolas' },
        { value: 'CGPI', label: 'CGPI - Coordenação Geral de Privilégios e Imunidades' },
        { value: 'CGPMAF', label: 'CGPMAF - Coordenadoria Geral de Polícia Marítima, Aeronáutica e de Fronteiras' },
        { value: 'CIPC', label: 'CIPC - Centro de Inteligência da Polícia Civil' },
        { value: 'CNIG', label: 'CNIG - Conselho Nacional de Imigração' },
        { value: 'CNT', label: 'CNT - Confederação Nacional do Transporte' },
        { value: 'CNTV', label: 'CNTV - Confederação Nacional de Vigilantes & Prestadores de Serviços' },
        { value: 'COFECI', label: 'COFECI - Conselho Federal de Corretores de Imóveis' },
        { value: 'COFECON', label: 'COFECON - Conselho Federal de Economia' },
        { value: 'COFEM', label: 'COFEM - Conselho Federal de Museologia' },
        { value: 'COFEN', label: 'COFEN - Conselho Federal de Enfermagem' },
        { value: 'COFFITO', label: 'COFFITO - Conselho Regional de Fisioterapia e Terapia Ocupacional' },
        { value: 'COMAER', label: 'COMAER - Comando da Aeronáutica' },
        { value: 'CONFE', label: 'CONFE - Conselho Federal de Estatística' },
        { value: 'CONFEA', label: 'CONFEA - Conselho Federal de Engenharia e Agronomia' },
        { value: 'CONFEF', label: 'CONFEF - Conselho Federal de Educação Física' },
        { value: 'CONFERE', label: 'CONFERE - Conselho Federal dos Representantes Comerciais' },
        { value: 'CONRE', label: 'CONRE - Conselho Regional de Estatística' },
        { value: 'CONRERP', label: 'CONRERP - Conselho Federal de Profissionais de Relações Públicas' },
        { value: 'CORE', label: 'CORE - Conselho Regional dos Representantes Comerciais' },
        { value: 'CORECON', label: 'CORECON - Conselho Regional de Economia' },
        { value: 'COREM', label: 'COREM - Conselho Regional de Museologia' },
        { value: 'COREN', label: 'COREN - Conselho Regional de Enfermagem' },
        { value: 'CRA', label: 'CRA - Conselho Regional de Administração' },
        { value: 'CRAS', label: 'CRAS - Centro de Referência de Assistência Social' },
        { value: 'CRB', label: 'CRB - Conselho Regional de Biblioteconomia' },
        { value: 'CRBIO', label: 'CRBIO - Conselho Regional de Biologia' },
        { value: 'CRBM', label: 'CRBM - Conselho Regional de Biomedicina' },
        { value: 'CRC', label: 'CRC - Conselho Regional de Contabilidade' },
        { value: 'CREA', label: 'CREA - Conselho Regional de Engenharia e Agronomia' },
        { value: 'CRECI', label: 'CRECI - Conselho Regional de Corretores de Imóveis' },
        { value: 'CREF', label: 'CREF - Conselho Regional de Educação Física' },
        { value: 'CREFITO', label: 'CREFITO - Conselho Regional de Fisioterapia e Terapia Ocupacional' },
        { value: 'CRESS', label: 'CRESS - Conselho Regional de Serviço Social' },
        { value: 'CRF', label: 'CRF - Conselho Regional de Farmácia' },
        { value: 'CRFA', label: 'CRFA - Conselho Regional de Fonoaudiologia' },
        { value: 'CRM', label: 'CRM - Conselho Regional de Medicina' },
        { value: 'CRMV', label: 'CRMV - Conselho Regional de Medicina Veterinária' },
        { value: 'CRN', label: 'CRN - Conselho Regional de Nutrição' },
        { value: 'CRO', label: 'CRO - Conselho Regional de Odontologia' },
        { value: 'CRP', label: 'CRP - Conselho Regional de Psicologia' },
        { value: 'CRPRE', label: 'CRPRE - Conselho Regional de Profissionais de Relações Públicas' },
        { value: 'CRQ', label: 'CRQ - Conselho Regional de Química' },
        { value: 'CRT', label: 'CRT - Conselho Regional dos Técnicos Industriais' },
        { value: 'CRTA', label: 'CRTA - Conselho Regional de Técnicos de Administração' },
        { value: 'CTPS', label: 'CTPS - Carteira de Trabalho e Previdência Social' },
        { value: 'CV', label: 'CV - Cartório Civil' },
        { value: 'DELEMIG', label: 'DELEMIG - Delegacia de Polícia de Imigração' },
        { value: 'DETRAN', label: 'DETRAN - Departamento Estadual de Trânsito' },
        { value: 'DGPC', label: 'DGPC - Diretoria Geral da Polícia Civil' },
        { value: 'DIC', label: 'DIC - Diretoria de Identificação Civil' },
        { value: 'DICC', label: 'DICC - Diretoria de Identificação Civil e Criminal' },
        { value: 'DIREX', label: 'DIREX - Diretoria Executiva' },
        { value: 'DPF', label: 'DPF - Departamento de Polícia Federal' },
        { value: 'DPMAF', label: 'DPMAF - Divisão de Polícia Marítima, Aérea e de Fronteiras' },
        { value: 'DPT', label: 'DPT - Departamento de Polícia Técnica Geral' },
        { value: 'DPTC', label: 'DPTC - Departamento de Polícia Técnico Científica' },
        { value: 'DREX', label: 'DREX - Delegacia Regional Executiva' },
        { value: 'DRT', label: 'DRT - Delegacia Regional do Trabalho' },
        { value: 'EB', label: 'EB - Exército Brasileiro' },
        { value: 'FAB', label: 'FAB - Força Aérea Brasileira' },
        { value: 'FENAJ', label: 'FENAJ - Federação Nacional dos Jornalistas' },
        { value: 'FGTS', label: 'FGTS - Fundo de Garantia do Tempo de Serviço' },
        { value: 'FIPE', label: 'FIPE - Fundação Instituto de Pesquisas Econômicas' },
        { value: 'FLS', label: 'FLS - Fundação Lyndolpho Silva' },
        { value: 'FUNAI', label: 'FUNAI - Fundação Nacional do Índio' },
        { value: 'GEJSP', label: 'GEJSP - Gerência de Estado de Justiça, Segurança Pública e Cidadania' },
        { value: 'GEJSPC', label: 'GEJSPC - Gerência de Estado de Justiça, Segurança Pública e Cidadania' },
        { value: 'GEJUSPC', label: 'GEJUSPC - Gerência de Estado de Justiça, Segurança Pública e Cidadania' },
        { value: 'GESP', label: 'GESP - Gerência de Estado de Segurança Pública' },
        { value: 'GOVGO', label: 'GOVGO - Governo do Estado de Goiás' },
        { value: 'I CLA', label: 'I CLA - Carteira de Identidade Classista' },
        { value: 'ICP', label: 'ICP - Instituto de Polícia Científica' },
        { value: 'IDAMP', label: 'IDAMP - Instituto de Identificação Dr. Aroldo Mendes Paiva' },
        { value: 'IFP', label: 'IFP - Instituto Félix Pacheco' },
        { value: 'IGP', label: 'IGP - Instituto Geral de Perícias' },
        { value: 'IIACM', label: 'IIACM - Instituto de Identificação Aderson Conceição de Melo' },
        { value: 'IICC', label: 'IICC - Instituto de Identificação Civil e Criminal' },
        { value: 'IICCECF', label: 'IICCECF - Instituto de Identificação Civil e Criminal Engrácia da Costa Francisco' },
        { value: 'IICM', label: 'IICM - Instituto de Identificação Carlos Menezes' },
        { value: 'IIGP', label: 'IIGP - Instituto de Identificação Gonçalo Pereira' },
        { value: 'IIJDM', label: 'IIJDM - Instituto de Identificação João de Deus Martins' },
        { value: 'IIPC', label: 'IIPC - Instituto de Identificação da Polícia Civil' },
        { value: 'IIRGD', label: 'IIRGD - Instituto de Identificação Ricardo Gumbleton Daunt' },
        { value: 'IIRHM', label: 'IIRHM - Instituto de Identificação Raimundo Hermínio de Melo' },
        { value: 'IITB', label: 'IITB - Instituto de Identificação Tavares Buril' },
        { value: 'IML', label: 'IML - Instituto Médico-Legal' },
        { value: 'INI', label: 'INI - Instituto Nacional de Identificação' },
        { value: 'IPF', label: 'IPF - Instituto Pereira Faustino' },
        { value: 'ITCP', label: 'ITCP - Instituto Técnico-Científico de Perícia' },
        { value: 'ITEP', label: 'ITEP - Instituto Técnico-Científico de Perícia' },
        { value: 'MAER', label: 'MAER - Ministério da Aeronáutica' },
        { value: 'MB', label: 'MB - Marinha do Brasil' },
        { value: 'MD', label: 'MD - Ministério da Defesa' },
        { value: 'MDS', label: 'MDS - Ministério da Cidadania' },
        { value: 'MEC', label: 'MEC - Ministério da Educação e Cultura' },
        { value: 'MEX', label: 'MEX - Ministério do Exército' },
        { value: 'MINDEF', label: 'MINDEF - Ministério da Defesa' },
        { value: 'MJ', label: 'MJ - Ministério da Justiça' },
        { value: 'MM', label: 'MM - Ministério da Marinha' },
        { value: 'MMA', label: 'MMA - Ministério da Marinha' },
        { value: 'MPAS', label: 'MPAS - Ministério da Previdência e Assistência Social' },
        { value: 'MPE', label: 'MPE - Ministério Público Estadual' },
        { value: 'MPF', label: 'MPF - Ministério Público Federal' },
        { value: 'MPT', label: 'MPT - Ministério Público do Trabalho' },
        { value: 'MRE', label: 'MRE - Ministério das Relações Exteriores' },
        { value: 'MT', label: 'MT - Ministério do Trabalho' },
        { value: 'MTE', label: 'MTE - Ministério da Economia' },
        { value: 'MTPS', label: 'MTPS - Ministério do Trabalho e Previdência Social' },
        { value: 'NUMIG', label: 'NUMIG - Núcleo de Polícia de Imigração' },
        { value: 'OAB', label: 'OAB - Ordem dos Advogados do Brasil' },
        { value: 'OMB', label: 'OMB - Ordens dos Músicos do Brasil' },
        { value: 'PC', label: 'PC - Polícia Civil' },
        { value: 'PF', label: 'PF - Polícia Federal' },
        { value: 'PGFN', label: 'PGFN - Procuradoria Geral da Fazenda Nacional' },
        { value: 'PM', label: 'PM - Polícia Militar' },
        { value: 'POLITEC', label: 'POLITEC - Perícia Oficial e Identificação Técnica' },
        { value: 'PRF', label: 'PRF - Polícia Rodoviária Federal' },
        { value: 'PTC', label: 'PTC - Polícia Tecnico-Científica' },
        { value: 'SCC', label: 'SCC - Secretaria de Estado da Casa Civil' },
        { value: 'SCJDS', label: 'SCJDS - Secretaria Coordenadora de Justiça e Defesa Social' },
        { value: 'SDS', label: 'SDS - Secretaria de Defesa Social' },
        { value: 'SECC', label: 'SECC - Secretaria de Estado da Casa Civil' },
        { value: 'SECCDE', label: 'SECCDE - Secretaria de Estado da Casa Civil e Desenvolvimento Econômico' },
        { value: 'SEDS', label: 'SEDS - Secretaria de Estado da Defesa Social' },
        { value: 'SEGUP', label: 'SEGUP - Secretaria de Estado da Segurança Pública e da Defesa Social' },
        { value: 'SEJSP', label: 'SEJSP - Secretaria de Estado de Justiça e Segurança Pública' },
        { value: 'SEJUC', label: 'SEJUC - Secretaria de Estado da Justica' },
        { value: 'SEJUSP', label: 'SEJUSP - Secretaria de Estado de Justiça e Segurança Pública' },
        { value: 'SEPC', label: 'SEPC - Secretaria de Estado da Polícia Civil' },
        { value: 'SEPOL', label: 'SEPOL - Secretaria de Estado de Polícia Civil' },
        { value: 'SES', label: 'SES - Secretaria de Estado da Segurança' },
        { value: 'SESC', label: 'SESC - Secretaria de Estado da Segurança e Cidadania' },
        { value: 'SESDC', label: 'SESDC - Secretaria de Estado da Segurança, Defesa e Cidadania' },
        { value: 'SESDEC', label: 'SESDEC - Secretaria de Estado da Segurança, Defesa e Cidadania' },
        { value: 'SESEG', label: 'SESEG - Secretaria Estadual de Segurança' },
        { value: 'SESP', label: 'SESP - Secretaria de Estado da Segurança Pública' },
        { value: 'SESPAP', label: 'SESPAP - Secretaria de Estado da Segurança Pública e Administração Penitenciária' },
        { value: 'SESPDC', label: 'SESPDC - Secretaria de Estado de Segurança Publica e Defesa do Cidadão' },
        { value: 'SESPDS', label: 'SESPDS - Secretaria de Estado de Segurança Pública e Defesa Social' },
        { value: 'SGPC', label: 'SGPC - Superintendência Geral de Polícia Civil' },
        { value: 'SGPJ', label: 'SGPJ - Superintendência Geral de Polícia Judiciária' },
        { value: 'SIM', label: 'SIM - Serviço de Identificação da Marinha' },
        { value: 'SJ', label: 'SJ - Secretaria da Justiça' },
        { value: 'SJCDH', label: 'SJCDH - Secretaria da Justiça e dos Direitos Humanos' },
        { value: 'SJDS', label: 'SJDS - Secretaria Coordenadora de Justiça e Defesa Social' },
        { value: 'SJS', label: 'SJS - Secretaria da Justiça e Segurança' },
        { value: 'SJTC', label: 'SJTC - Secretaria da Justiça do Trabalho e Cidadania' },
        { value: 'SJTS', label: 'SJTS - Secretaria da Justiça do Trabalho e Segurança' },
        { value: 'SNJ', label: 'SNJ - Secretaria Nacional de Justiça / Departamento de Estrangeiros' },
        { value: 'SPMAF', label: 'SPMAF - Serviço de Polícia Marítima, Aérea e de Fronteiras' },
        { value: 'SPTC', label: 'SPTC - Secretaria de Polícia Técnico-Científica' },
        { value: 'SRDPF', label: 'SRDPF - Superintendência Regional do Departamento de Polícia Federal' },
        { value: 'SRF', label: 'SRF - Receita Federal' },
        { value: 'SRTE', label: 'SRTE - Superintendência Regional do Trabalho' },
        { value: 'SSDC', label: 'SSDC - Secretaria da Segurança, Defesa e Cidadania' },
        { value: 'SSDS', label: 'SSDS - Secretaria da Segurança e da Defesa Social' },
        { value: 'SSI', label: 'SSI - Secretaria de Segurança e Informações' },
        { value: 'SSP', label: 'SSP - Secretaria de Segurança Pública' },
        { value: 'SSPCGP', label: 'SSPCGP - Secretaria de Segurança Pública e Coordenadoria Geral de Perícias' },
        { value: 'SSPDC', label: 'SSPDC - Secretaria de Segurança Pública e Defesa do Cidadão' },
        { value: 'SSPDS', label: 'SSPDS - Secretaria de Segurança Pública e Defesa Social' },
        { value: 'SSPPC', label: 'SSPPC - Secretaria de Segurança Pública Polícia Civil' },
        { value: 'SUSEP', label: 'SUSEP - Superintendência de Seguros Privados' },
        { value: 'SUSEPE', label: 'SUSEPE - Superintendência dos Serviços Penitenciários' },
        { value: 'TJ', label: 'TJ - Tribunal de Justiça' },
        { value: 'TJAEM', label: 'TJAEM - Tribunal Arbitral e Mediação dos Estados Brasileiros' },
        { value: 'TRE', label: 'TRE - Tribunal Regional Eleitoral' },
        { value: 'TRF', label: 'TRF - Tribunal Regional Federal' },
        { value: 'TSE', label: 'TSE - Tribunal Superior Eleitoral' },
        { value: 'XXX', label: 'XXX - Orgão Estrangeiro' },
        { value: 'ZZZ', label: 'ZZZ - Outro' },
    ];

    get label(): string {
        return profileTypeStrategies[this.config.type] || 'Usuários';
    }

    get hasTechnicalDataTab(): boolean {
        return PROFILES_WITH_TECHNICAL_DATA.includes(this.config.type);
    }

    get isNurseLike(): boolean {
        return isNurseLikeProfile(this.config.type);
    }

    /** Rótulo de origem (PAINEL vs formulário) para exibir com autoTranslate no template. */
    get originLabel(): string {
        return this.form?.get('origin')?.value === 'PAINEL' ? 'Interno' : 'Formulário';
    }

    minAgeValidator(minAge: number) {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;
            if (!value) return null;
            const birthDate = new Date(value);
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--;
            }
            return age < minAge ? { minAge: { requiredAge: minAge, actualAge: age } } : null;
        };
    }

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data:
            | (UserModel & {
                  config?: {
                      type: UserProfileType;
                      isProfileEdit?: boolean;
                  };
              })
            | null,
        private fb: FormBuilder,
        private service: UserService,
        private route: ActivatedRoute,
        private dialogRef: MatDialogRef<UserFormComponent>,
        private permissionGroupService: PermissionGroupService,
        private scheduleService: ScheduleService,
        private companyService: CompanyService,
        private healthPlanService: HealthPlanService,
        private cepService: CepService,
        private _devstroupeConfirmationService: DevstroupeConfirmationService,
        private cfmService: CFMService,
        private userCompanyService: UserCompanyService,
        private doctorService: DoctorService,
        private doctorCrmService: DoctorCrmService,
        private dialog: MatDialog,
        private cdr: ChangeDetectorRef,
        private permissionService: PermissionService,
        private errorDialogService: ErrorDialogService,
        private integrationService: IntegrationService,
        private languageService: LanguageService,
    ) {
        super();
        this.dialogRef.addPanelClass('card-dialog');
        this.config = data?.config || { type: 'DOCTOR', isProfileEdit: false };
        const rawKind = (data as { company_kind?: string } | null)?.company_kind;
        this.contextCompanyKind = rawKind ? this.normalizePermissionCompanyKind(rawKind) : null;
        const rawCompanyId = Number((data as { company_id?: number } | null)?.company_id);
        this.contextCompanyId = Number.isFinite(rawCompanyId) && rawCompanyId > 0 ? rawCompanyId : null;
    }

    private getProfileTypeInternal(): string {
        // Determinar o perfil do usuário sendo editado
        const profileType = this.data?.profile_type || this.config.type;
        return profileType?.toUpperCase() || this.config.type;
    }

    // Método público para uso no template
    getProfileType(): string {
        return this.getProfileTypeInternal();
    }

    private getPermissionPrefix(): string {
        const profileType = this.getProfileTypeInternal();
        switch (profileType) {
            case 'DOCTOR':
                return 'DOCTORS';
            case 'NURSE':
                return 'NURSES';
            case 'NURSING_TECH':
                return 'NURSING_TECHS';
            case 'DRIVER':
                return 'DRIVERS';
            case 'PARAMEDIC':
                return 'PARAMEDICS';
            case 'ADMIN':
                return 'ADMIN';
            case 'COMPANY_ADMIN':
                return 'COMPANY_ADMIN';
            case 'PATIENT':
                return 'PATIENT';
            default:
                return '';
        }
    }

    get canCreate(): boolean {
        const prefix = this.getPermissionPrefix();
        return prefix ? this.permissionService.hasPermission(`${prefix}_CREATE`) : false;
    }

    get canView(): boolean {
        const prefix = this.getPermissionPrefix();
        return prefix ? this.permissionService.hasPermission(`${prefix}_VIEW`) : false;
    }

    get canEdit(): boolean {
        const prefix = this.getPermissionPrefix();
        return prefix ? this.permissionService.hasPermission(`${prefix}_UPDATE`) : false;
    }

    get canDelete(): boolean {
        const prefix = this.getPermissionPrefix();
        return prefix ? this.permissionService.hasPermission(`${prefix}_DELETE`) : false;
    }

    // Métodos públicos para uso no template - retornam a permissão específica do perfil atual
    getDeletePermission(): string {
        const prefix = this.getPermissionPrefix();
        return prefix ? `${prefix}_DELETE` : '';
    }

    getUpdatePermission(): string {
        const prefix = this.getPermissionPrefix();
        return prefix ? `${prefix}_UPDATE` : '';
    }

    getCreatePermission(): string {
        const prefix = this.getPermissionPrefix();
        return prefix ? `${prefix}_CREATE` : '';
    }

    resendResetPassword(): void {
        const user_id = this.form.get('user_id')?.value;
        if (!user_id) return;
        this.sendingResetPassword = true;
        this.service
            .sendResetPasswordLink(user_id)
            .pipe(take(1), finalize(() => (this.sendingResetPassword = false)))
            .subscribe({
                next: () => {
                    if (typeof alert === 'function') alert('Link de redefinição de senha enviado por e-mail para o usuário.');
                },
                error: (err) => {
                    const msg = err?.error?.message || err?.message || 'Erro ao enviar. Tente novamente.';
                    if (typeof alert === 'function') alert(msg);
                },
            });
    }

    ngOnInit(): void {
        console.log('Config', this.config);
        if (this.config.type) {
            this.form.controls['profile_type'].setValue(this.config.type);
        }

        if (this.config.type === 'ADMIN') {
            this.form.controls['permission_group_id'].clearValidators();
            this.form.controls['permission_group_id'].updateValueAndValidity();
            this.form.controls['company_id'].setValue(null);
        }

        if (this.config.type === 'PATIENT') {
            this.form.controls['name'].setValidators([Validators.required]);
            this.form.controls['num_doc'].clearValidators();
            this.form.controls['email'].clearValidators();
            this.form.controls['password'].clearValidators();
            // Paciente sempre nasce vinculado a uma empresa (quem o cadastra).
            this.form.controls['company_id'].setValidators([Validators.required]);
            this.form.controls['company_id'].updateValueAndValidity();
            if (this.showPatientCompanySelect) {
                this.companyService.list({ limit: 'all', company_kind: 'client' } as any).pipe(take(1)).subscribe((res) => {
                    this.patientCompanies = res.items || [];
                });
            }
        } else {
            // Data de nascimento opcional para funcionários; se preenchida, exige idade mínima 18.
            this.form.get('birthday_date')?.setValidators([this.minAgeValidator(18)]);
            this.form.get('birthday_date')?.updateValueAndValidity();
        }

        // Adicionar validador de padrão para CRM quando for DOCTOR
        if (this.config.type === 'DOCTOR') {
            const crmControl = this.form.get('professional_number');
            if (crmControl) {
                // Obrigatoriedade do CRM depende do país da empresa selecionada (BR = obrigatório)
                this.applyCrmRequirementByCountry();

                // Adicionar listener para mudanças no CRM
                crmControl.valueChanges
                    .pipe(
                        debounceTime(800),
                        distinctUntilChanged(),
                        filter((value) => {
                            // Só consultar se o formato estiver completo
                            return value && /^\d+-[A-Z]{2}$/.test(value);
                        }),
                        takeUntil(this._unsubscribeAll)
                    )
                    .subscribe(() => {
                        this.consultMedicAutomatically();
                    });
            }
        }

        this.companyService.currentCompany$.pipe(takeUntil(this._unsubscribeAll)).subscribe((company) => {
            const presetCompanyId = Number((this.data as { company_id?: number })?.company_id);
            if (presetCompanyId && this.config.type !== 'ADMIN') {
                this.form.controls['company_id'].setValue(presetCompanyId);
            } else if (!this.isAdmin && ['DOCTOR', 'NURSE', 'NURSING_TECH', 'DRIVER', 'PARAMEDIC'].includes(this.config.type)) {
                this.form.controls['company_id'].setValue(company.company_id);
            } else if (!this.isAdmin && this.config.type === 'PATIENT') {
                this.form.controls['company_id'].setValue(company.company_id);
            }

            if (this.config.type === 'DOCTOR') {
                this.applyCrmRequirementByCountry();
            }
        });

        const presetCompanyId = Number((this.data as { company_id?: number })?.company_id);
        if (presetCompanyId && !this.data?.user_id && this.config.type !== 'ADMIN') {
            this.form.controls['company_id'].setValue(presetCompanyId);
            this.loadUserCompanies();
        } else if (
            !this.data?.user_id &&
            !this.isAdmin &&
            ['DOCTOR', 'NURSE', 'NURSING_TECH', 'DRIVER', 'PARAMEDIC'].includes(this.config.type)
        ) {
            const company = this.companyService.getCurrentCompany();
            if (company?.company_id) {
                this.form.controls['company_id'].setValue(company.company_id);
                this.loadUserCompanies();
            }
        }

        // Carregar usuário atual (pegar valor atual do BehaviorSubject)
        this.currentUser = ApiService.user.value;

        // Se não tiver valor, tentar pegar do Observable
        if (!this.currentUser) {
            ApiService.userCurrent
                .pipe(
                    filter((user) => user !== null),
                    take(1),
                    takeUntil(this._unsubscribeAll),
                )
                .subscribe((user) => {
                    this.currentUser = user;
                    this.cdr.detectChanges();
                });
        } else {
            // Se já tiver valor, forçar detecção de mudanças
            this.cdr.detectChanges();
        }

        // Também se inscrever para mudanças futuras
        ApiService.userCurrent.pipe(takeUntil(this._unsubscribeAll)).subscribe((user) => {
            if (user) {
                this.currentUser = user;
                this.cdr.detectChanges();
            }
        });

        this.getUser();
        this.getPermissionGroups();
        this.getHealthPlans();
        this.loadWhatsAppIntegrations();
        this.onBirthdayDateChange();
        this.onCepChange();
        this.onEmailChange();

        // Adicionar listener para mudanças no permission_group_id para validar senha
        this.form.controls['permission_group_id'].valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.updatePasswordValidation();
            });

        this.form.controls['company_id'].valueChanges
            .pipe(distinctUntilChanged(), takeUntil(this._unsubscribeAll))
            .subscribe((companyId) => {
                this.getPermissionGroups();
                if (this.showRegimeSelect) {
                    this.loadRegimes(companyId);
                }
            });

        if (this.showRegimeSelect) {
            this.loadRegimes(this.form.controls['company_id'].value);
        }
    }

    /**
     * `true` quando a empresa selecionada na sidenav é do Brasil (ISO2 = BR).
     * País ausente/indefinido é tratado como BR (normalizeCountryCode), mantendo o CRM obrigatório por padrão.
     */
    isCurrentCompanyBrazil(): boolean {
        const company = this.companyService.getCurrentCompany();
        return normalizeCountryCode(company?.country) === 'BR';
    }

    /**
     * Ajusta a obrigatoriedade do CRM (professional_number) conforme o país da empresa atual.
     * Brasil: obrigatório + formato NÚMERO-UF. Demais países: apenas o formato quando preenchido.
     */
    applyCrmRequirementByCountry(): void {
        if (this.config.type !== 'DOCTOR') return;
        const crmControl = this.form.get('professional_number');
        if (!crmControl) return;

        const validators = [Validators.pattern(/^\d+-[A-Z]{2}$/)];
        if (this.isCurrentCompanyBrazil()) {
            validators.unshift(Validators.required);
        }
        crmControl.setValidators(validators);
        crmControl.updateValueAndValidity();
    }

    /**
     * Verifica se o permission_group atual tem a permissão SYSTEM_ACCESS
     */
    hasSystemAccess(): boolean {
        const permissionGroupId = this.form.controls['permission_group_id'].value;
        if (!permissionGroupId) {
            return false;
        }
        
        const permissionGroup = this.permissionGroups.find(pg => pg.permission_group_id === permissionGroupId);
        if (!permissionGroup || !permissionGroup.permissions) {
            return false;
        }

        return permissionGroup.permissions.some(p => p.code === 'SYSTEM_ACCESS');
    }

    /**
     * Atualiza a validação de senha baseada em SYSTEM_ACCESS.
     * Senha é sempre opcional - serve para controlar se o campo é exibido (pode logar) ou não.
     */
    updatePasswordValidation(): void {
        const passwordControl = this.form.controls['password'];
        const hasSystemAccess = this.hasSystemAccess();
        
        if (hasSystemAccess) {
            // Mostrar campo de senha, mas nunca obrigatório
            passwordControl.clearValidators();
        } else {
            // Sem SYSTEM_ACCESS: não exibe/usa senha, limpar valor
            passwordControl.clearValidators();
            passwordControl.setValue(null);
        }
        
        passwordControl.updateValueAndValidity();
    }

    onEmailChange(): void {
        this.form.controls['email'].valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((value) => {
            if (value && typeof value === 'string' && value.toLowerCase().startsWith('mailto:')) {
                const cleanedEmail = value.replace(/^mailto:/i, '').trim();
                this.form.controls['email'].setValue(cleanedEmail, { emitEvent: false });
            }
        });
    }

    /** Indica se a aba tem campos inválidos (última tentativa de salvar). */
    tabSectionHasInvalid(section: UserFormTabKey): boolean {
        return this.invalidTabSections.has(section);
    }

    private collectInvalidControlPaths(control: AbstractControl, path = ''): string[] {
        if (control instanceof FormGroup) {
            return Object.keys(control.controls).flatMap((key) => {
                const child = control.get(key);
                if (!child) {
                    return [] as string[];
                }
                const nextPath = path ? `${path}.${key}` : key;
                return this.collectInvalidControlPaths(child, nextPath);
            });
        }
        if (control instanceof FormArray) {
            return control.controls.flatMap((ctrl, i) => this.collectInvalidControlPaths(ctrl, `${path}[${i}]`));
        }
        return control.invalid ? [path] : [];
    }

    private getLeafKeyFromPath(path: string): string {
        const normalized = path.replace(/\[\d+\]/g, '');
        const segments = normalized.split('.').filter(Boolean);
        return segments[segments.length - 1] || path;
    }

    private getSectionForControlPath(path: string): UserFormTabKey {
        if (path.startsWith('notificationPreferences')) {
            return 'notifications';
        }
        const leaf = this.getLeafKeyFromPath(path);
        if (USER_FORM_ATTACHMENT_FIELD_KEYS.has(leaf)) {
            return 'attachments';
        }
        if (USER_FORM_OTHER_INFO_FIELD_KEYS.has(leaf)) {
            return this.config.type === 'DOCTOR' ? 'other' : 'general';
        }
        if (USER_FORM_TECHNICAL_FIELD_KEYS.has(leaf)) {
            return 'technical';
        }
        return 'general';
    }

    /** Ordem idêntica aos mat-tab no template (cada *ngIf). */
    private getVisibleTabKeys(): UserFormTabKey[] {
        const keys: UserFormTabKey[] = ['general'];
        if (this.config.type === 'DOCTOR' && this.data?.user_id) {
            keys.push('crm');
        }
        if (PROFILES_WITH_TECHNICAL_DATA.includes(this.config.type)) {
            keys.push('technical');
            keys.push('attachments');
        }
        if (this.config.type === 'DOCTOR') {
            keys.push('other');
        }
        if (this.showCompaniesTab) {
            keys.push('companies');
        }
        if (this.showNotificationsTab) {
            keys.push('notifications');
        }
        return keys;
    }

    private getSectionTabTitle(key: UserFormTabKey): string {
        const titles: Record<UserFormTabKey, string> = {
            general: 'Dados Gerais',
            crm: 'CRMs',
            technical: 'Dados Técnicos',
            attachments: 'Anexos',
            other: 'Outras Informações',
            companies: 'Empresas',
            notifications: 'Notificações',
        };
        return titles[key];
    }

    private buildValidationMessage(invalidPaths: string[], sections: Set<UserFormTabKey>): string {
        const visible = this.getVisibleTabKeys();
        const sectionTitles = [...sections]
            .filter((s) => visible.includes(s))
            .map((s) => this.getSectionTabTitle(s));
        const uniqueLabels = [...new Set(invalidPaths.map((p) => this.getFieldLabelForPath(p)))].slice(0, 12);
        const parts: string[] = ['Existem campos obrigatórios ou inválidos.'];
        if (sectionTitles.length) {
            parts.push(`Abas com pendências: ${sectionTitles.join(', ')}.`);
        }
        if (uniqueLabels.length) {
            const more = new Set(invalidPaths.map((p) => this.getFieldLabelForPath(p))).size > 12;
            parts.push(`Campos: ${uniqueLabels.join(', ')}${more ? '…' : '.'}`);
        }
        parts.push('Revise os campos destacados.');
        return parts.join(' ');
    }

    private getFieldLabelForPath(path: string): string {
        const leaf = this.getLeafKeyFromPath(path);
        if (path.startsWith('notificationPreferences.')) {
            const pref = leaf;
            return USER_FORM_FIELD_LABELS[pref] || `Notificação (${pref})`;
        }
        return USER_FORM_FIELD_LABELS[leaf] || leaf;
    }

    private applyInvalidFormFeedback(invalidPaths: string[]): void {
        const sectionsWithErrors = new Set(invalidPaths.map((p) => this.getSectionForControlPath(p)));
        this.invalidTabSections = sectionsWithErrors;

        if (this.config.type !== 'PATIENT') {
            const keys = this.getVisibleTabKeys();
            const firstIdx = keys.findIndex((k) => sectionsWithErrors.has(k));
            this.selectedTabIndex = firstIdx >= 0 ? firstIdx : 0;
        }

        const message = this.buildValidationMessage(invalidPaths, sectionsWithErrors);
        this.errorDialogService.show(message, 'warning');
        this.cdr.markForCheck();
    }

    done(): void {
        this.loading = true;
        this.form.markAllAsTouched();

        const isEditMode = !!this.form.controls['user_id'].value;

        if (!this.form.valid) {
            this.loading = false;
            const invalidPaths = this.collectInvalidControlPaths(this.form);
            this.applyInvalidFormFeedback(invalidPaths);
            return;
        }

        this.invalidTabSections.clear();

        let user: UserModel = this.form.getRawValue();

        if (!user.number_phone_country_code) {
            user.number_phone_country_code = DEFAULT_COUNTRY_CODE;
        }

        // Se a senha for o sentinela ou estiver vazia, não enviar (manter a senha existente)
        if (!user.password || user.password === this.PASSWORD_SENTINEL) {
            delete (user as any).password;
        }

        // Data de nascimento opcional: garantir null explícito (permite limpar valor já salvo)
        if (!user.birthday_date) {
            (user as any).birthday_date = null;
            (user as any).age = null;
        }

        if (this.shouldOmitDocumentFromUpdate(isEditMode, user.num_doc)) {
            delete (user as any).num_doc;
        }

        // Limpar "mailto:" do email se presente
        if (user.email && typeof user.email === 'string') {
            user.email = user.email.replace(/^mailto:/i, '').trim();
        }

        // Para COMPANY_ADMIN, definir is_admin como false
        if (this.config.type === 'COMPANY_ADMIN') {
            user.is_admin = false;
        }

        // Empresa do acordeão (ex.: solicitante): garantir company_id correto na validação do grupo
        if (this.contextCompanyId) {
            user.company_id = this.contextCompanyId;
        }

        // Para DOCTOR em modo edição: remover do payload de User os campos que ficam em Doctor/DoctorCrm
        // No create, enviar tudo para o backend criar Doctor + CRM a partir de professional_number
        if (this.config.type === 'DOCTOR' && isEditMode) {
            const {
                license_issue_date,
                license_expiry_date,
                license_issue_date_emission,
                graduation_institution,
                graduation_year,
                practice_area_or_courses,
                has_additional_courses,
                additional_courses_details,
                other_registration,
                telemedicine_experience,
                previous_aph_experience,
                shift_time_preferences,
                shift_day_preferences,
                shift_duration_preferences,
                average_hourly_rate,
                issuer_uf,
                registration,
                license_status,
                specialties_or_courses,
                professional_number,
                ...userDataWithoutDoctorFields
            } = user as any;
            user = userDataWithoutDoctorFields as UserModel;
        }

        // Validar permissões
        if (user.user_id) {
            const updatePermission = this.getUpdatePermission();
            if (!updatePermission || !this.permissionService.hasPermission(updatePermission)) {
                this.loading = false;
                this.errorDialogService.showPermissionError('Você não tem permissão para editar este tipo de usuário');
                return;
            }
            this.service
                .update(user)
                .pipe(
                    take(1),
                    finalize(() => (this.loading = false)),
                )
                .subscribe({
                    next: (updatedUser) => {
                        // Se for médico, atualizar registro em doctor
                        if (this.config.type === 'DOCTOR' && updatedUser.user_id) {
                            this.saveDoctor(updatedUser.user_id).then(() => {
                                this.dialogRef.close(true);
                            });
                        } else {
                            this.dialogRef.close(true);
                        }
                    },
                    error: () => {
                        // Error já será tratado pelo BaseService
                    },
                });
        } else {
            const createPermission = this.getCreatePermission();
            if (!createPermission || !this.permissionService.hasPermission(createPermission)) {
                this.loading = false;
                this.errorDialogService.showPermissionError('Você não tem permissão para criar este tipo de usuário');
                return;
            }
            const isNonAdminMedical =
                !this.isAdmin && ['DOCTOR', 'NURSE', 'NURSING_TECH', 'DRIVER', 'PARAMEDIC'].includes(this.config.type);
            const companyIdToAssociate = isNonAdminMedical
                ? this.companyService.getCurrentCompany()?.company_id
                : (this.form.get('company_id')?.value ?? this.companyService.getCurrentCompany()?.company_id);
            this.service
                .create(user)
                .pipe(
                    take(1),
                    finalize(() => (this.loading = false)),
                )
                .subscribe({
                    next: async (createdUser) => {
                        if (createdUser.user_id && companyIdToAssociate) {
                            try {
                                await firstValueFrom(
                                    this.userCompanyService
                                        .associate(createdUser.user_id, companyIdToAssociate)
                                        .pipe(take(1))
                                );
                                this.form.get('company_id')?.setValue(null);
                            } catch (err) {
                                console.error('Erro ao associar empresa:', err);
                            }
                        }
                        if (this.config.type === 'DOCTOR' && createdUser.user_id) {
                            await this.saveDoctor(createdUser.user_id);
                        }
                        this.dialogRef.close(true);
                    },
                    error: () => {
                        // Error já será tratado pelo BaseService
                    },
                });
        }
    }

    async saveDoctor(userId: number): Promise<void> {
        const doctorData: Partial<DoctorModel> = {
            user_id: userId,
            graduation_institution: this.form.get('graduation_institution')?.value,
            graduation_year: this.form.get('graduation_year')?.value,
            practice_area_or_courses: this.form.get('practice_area_or_courses')?.value,
            has_additional_courses: this.form.get('has_additional_courses')?.value,
            additional_courses_details: this.form.get('additional_courses_details')?.value,
            other_registration: this.form.get('other_registration')?.value,
            telemedicine_experience: this.form.get('telemedicine_experience')?.value,
            previous_aph_experience: this.form.get('previous_aph_experience')?.value,
            shift_time_preferences: this.form.get('shift_time_preferences')?.value,
            shift_day_preferences: this.form.get('shift_day_preferences')?.value,
            shift_duration_preferences: this.form.get('shift_duration_preferences')?.value,
            average_hourly_rate: this.form.get('average_hourly_rate')?.value,
            // Campos técnicos de datas salvos no doctor (não no user)
            license_issue_date: this.form.get('license_issue_date')?.value,
            license_expiry_date: this.form.get('license_expiry_date')?.value,
            license_issue_date_emission: this.form.get('license_issue_date_emission')?.value,
        };

        try {
            // Se o doctor já existe, atualizar
            if (this.doctor?.doctor_id) {
                await firstValueFrom(this.doctorService.updateDoctor(userId, doctorData));
            } else {
                // Se não existe, o backend cria automaticamente no create-user
                // Mas podemos tentar atualizar mesmo assim (o backend deve criar se não existir)
                // Ou podemos simplesmente ignorar pois o backend cria automaticamente
                // Por enquanto, vamos tentar buscar novamente e depois atualizar
                try {
                    const doctor = await firstValueFrom(this.doctorService.getDoctor(userId));
                    if (doctor && doctor.doctor_id) {
                        await firstValueFrom(this.doctorService.updateDoctor(userId, doctorData));
                    }
                    // Se não existir, o backend criou mas ainda não temos acesso
                    // Não fazer nada, será carregado na próxima vez
                } catch (error) {
                    // Doctor ainda não foi criado pelo backend, isso é esperado
                    // Não fazer nada
                }
            }
        } catch (error) {
            console.error('Erro ao salvar dados do médico:', error);
            // Não falhar a criação do user se houver erro ao criar doctor
        }
    }

    clear(): void {
        this.dialogRef.close(false);
    }

    deleteUser(): void {
        const deletePermission = this.getDeletePermission();
        if (!deletePermission || !this.permissionService.hasPermission(deletePermission)) {
            this.errorDialogService.showPermissionError('Você não tem permissão para deletar este tipo de usuário');
            return;
        }

        if (this.data) {
            this._devstroupeConfirmationService
                .open({
                    title: 'Deletar Usuário',
                    message: 'Tem certeza que deseja <strong>Deletar</strong> este Usuário?',
                    icon: {
                        show: true,
                        name: 'heroicons_outline:exclamation-circle',
                        color: 'warn',
                    },
                    actions: {
                        confirm: { show: true, label: 'Deletar', color: 'warn' },
                        cancel: { show: true, label: 'Cancelar' },
                    },
                    dismissible: true,
                })
                .afterClosed()
                .subscribe((result: boolean) => {
                    if (result) {
                        this.service
                            .delete(this.data)
                            .pipe(
                                take(1),
                                finalize(() => (this.loading = false)),
                            )
                            .subscribe(() => this.dialogRef.close(true));
                    }
                });
        }
    }

    loadWhatsAppIntegrations(): void {
        this.loadingIntegrations = true;
        this.integrationService
            .list({ limit: 'all' } as any)
            .pipe(
                take(1),
                finalize(() => (this.loadingIntegrations = false)),
            )
            .subscribe({
                next: (data) => {
                    this.whatsAppIntegrations = (data.items || []).filter((i: IntegrationModel) => i.type === 'WHATSAPP_BUSINESS');
                },
                error: () => {
                    this.whatsAppIntegrations = [];
                },
            });
    }

    getPermissionGroups(): void {
        const companyId =
            Number(this.form.controls['company_id'].value) ||
            this.contextCompanyId ||
            Number((this.data as { company_id?: number })?.company_id) ||
            null;
        const knownKind = this.resolveFormCompanyKind();

        // Com kind do contexto (acordeão), filtra direto — não depende de lookup.
        if (knownKind) {
            this.loadPermissionGroupsFiltered(knownKind);
            return;
        }

        const current = this.companyService.getCurrentCompany();
        const needsLookup = !!companyId && current?.company_id !== companyId;

        if (needsLookup) {
            this.companyService
                .list({ limit: 1, company_id: companyId } as any)
                .pipe(take(1))
                .subscribe({
                    next: (res) => {
                        const raw = res.items?.[0]?.company_kind;
                        const kind = raw ? this.normalizePermissionCompanyKind(raw) : null;
                        if (kind) {
                            this.contextCompanyKind = kind;
                        }
                        this.loadPermissionGroupsFiltered(kind);
                    },
                    error: () => this.loadPermissionGroupsFiltered(null),
                });
            return;
        }

        this.loadPermissionGroupsFiltered(knownKind);
    }

    private loadPermissionGroupsFiltered(companyKind: string | null | undefined): void {
        const filter: Record<string, unknown> = { limit: 'all' as const };

        if (this.config.type === 'ADMIN') {
            // Administrador global: somente grupos profile_type=ADMIN (sem company_kind)
            filter.profile_type = 'ADMIN';
        } else {
            if (this.config.type) {
                filter.profile_type = this.config.type;
            }
            if (companyKind) {
                filter.company_kind = companyKind;
            }
        }

        this.permissionGroupService
            .list(filter as any)
            .pipe(take(1))
            .subscribe((data) => {
                const items = this.mergeAssignedPermissionGroup(data.items || []);
                this.permissionGroups = items;
                this.applyPermissionGroupSelection(items);
                this.updatePasswordValidation();
            });
    }

    /** Mantém o grupo já salvo no dropdown mesmo fora do filtro da aba (ex.: legado NURSE → NURSING_TECH). */
    private mergeAssignedPermissionGroup(items: PermissionGroupModel[]): PermissionGroupModel[] {
        const currentId = this.form.controls['permission_group_id'].value;
        if (!currentId || items.some((g) => g.permission_group_id === currentId)) {
            return items;
        }

        const assigned = (this.data as UserModel)?.permission_group;
        if (assigned?.permission_group_id === currentId) {
            return [assigned as PermissionGroupModel, ...items];
        }

        return items;
    }

    private applyPermissionGroupSelection(items: PermissionGroupModel[]): void {
        const currentId = this.form.controls['permission_group_id'].value;
        const isEdit = !!this.data?.user_id;
        const defaultGroup = items.find((g) => g.profile_type === this.config.type) || items[0];

        if (currentId && items.some((g) => g.permission_group_id === currentId)) {
            return;
        }

        if (isEdit) {
            // Edição: não simula grupo salvo; só sugere default para perfis operacionais sem grupo.
            if (
                !currentId &&
                this.config.type !== 'ADMIN' &&
                ['DOCTOR', 'NURSE', 'NURSING_TECH', 'DRIVER', 'PARAMEDIC', 'COMPANY_ADMIN'].includes(this.config.type) &&
                defaultGroup
            ) {
                this.form.controls['permission_group_id'].setValue(defaultGroup.permission_group_id);
            }
            return;
        }

        if (!currentId && defaultGroup) {
            this.form.controls['permission_group_id'].setValue(defaultGroup.permission_group_id);
        }
    }

    /** Normaliza company_kind para filtro de permission_group (client ≡ manager). */
    private normalizePermissionCompanyKind(kind?: string | null): string {
        if (!kind || kind === 'client') {
            return 'manager';
        }
        return kind;
    }

    /**
     * Resolve o company_kind da empresa do formulário (preset accordion, empresa atual ou select).
     * Nunca usa a empresa da sidenav quando o company_id do form é de outra empresa.
     */
    private resolveFormCompanyKind(): string | null {
        if (this.contextCompanyKind) {
            return this.contextCompanyKind;
        }

        const dataKind = (this.data as { company_kind?: string })?.company_kind;
        if (dataKind) {
            return this.normalizePermissionCompanyKind(dataKind);
        }

        const companyId =
            Number(this.form.controls['company_id'].value) ||
            this.contextCompanyId ||
            Number((this.data as { company_id?: number })?.company_id) ||
            null;

        const current = this.companyService.getCurrentCompany();

        if (companyId) {
            if (current?.company_id === companyId) {
                return this.normalizePermissionCompanyKind(current.company_kind);
            }

            const fromPatient = this.patientCompanies.find((c) => c.company_id === companyId);
            if (fromPatient?.company_kind) {
                return this.normalizePermissionCompanyKind(fromPatient.company_kind);
            }

            const dataCompany = (this.data as { company?: CompanyModel })?.company;
            if (dataCompany?.company_id === companyId && dataCompany.company_kind) {
                return this.normalizePermissionCompanyKind(dataCompany.company_kind);
            }

            const fromUserCompanies = this.companiesDataSource?.data?.find(
                (uc) => uc.company?.company_id === companyId || (uc as any).company_id === companyId,
            );
            if (fromUserCompanies?.company?.company_kind) {
                return this.normalizePermissionCompanyKind(fromUserCompanies.company.company_kind);
            }

            // Empresa do form ≠ sidenav e ainda sem kind conhecido: não assumir manager da sidenav
            return null;
        }

        return current?.company_kind ? this.normalizePermissionCompanyKind(current.company_kind) : null;
    }

    getHealthPlans(): void {
        this.healthPlanService
            .list({ limit: 'all' })
            .pipe(take(1))
            .subscribe((data) => {
                this.plans = data.items;
            });
    }

    loadRegimes(companyId: number | null | undefined): void {
        if (!companyId) {
            this.regimes = [];
            return;
        }
        this.loadingRegimes = true;
        this.scheduleService
            .listRegimes(companyId)
            .pipe(
                take(1),
                finalize(() => (this.loadingRegimes = false)),
            )
            .subscribe({
                next: (data) => {
                    this.regimes = data || [];
                },
                error: () => {
                    this.regimes = [];
                },
            });
    }

    private applyDocumentFieldAccess(user: UserModel): void {
        const numDocControl = this.form.controls['num_doc'];
        const isEdit = !!user.user_id;

        if (isEdit && !this.canViewFullDocument) {
            numDocControl.clearValidators();
            numDocControl.setValue(this.DOC_UNCHANGED_SENTINEL, { emitEvent: false });
            numDocControl.updateValueAndValidity({ emitEvent: false });
            return;
        }

        if (this.config.type === 'PATIENT') {
            numDocControl.clearValidators();
        } else {
            numDocControl.setValidators([Validators.required]);
        }
        numDocControl.updateValueAndValidity({ emitEvent: false });
    }

    private shouldOmitDocumentFromUpdate(isEditMode: boolean, numDoc: string | null | undefined): boolean {
        if (!isEditMode) {
            return false;
        }
        if (!this.canViewFullDocument) {
            return true;
        }
        if (!numDoc || numDoc === this.DOC_UNCHANGED_SENTINEL) {
            return true;
        }
        return String(numDoc).includes('*');
    }

    getUser(): void {
        const id = this.data?.user_id;

        if (!id) {
            return;
        }

        this.loadingData = true;
        this.service
            .getById(+id)
            .pipe(
                take(1),
                finalize(() => (this.loadingData = false)),
            )
            .subscribe((user) => {
                // Preservar contexto do acordeão — this.data = user apagava company_kind
                const preservedKind =
                    this.contextCompanyKind || (this.data as { company_kind?: string })?.company_kind || null;
                const preservedConfig = this.config;
                this.data = {
                    ...user,
                    ...(preservedKind ? { company_kind: preservedKind } : {}),
                    config: preservedConfig,
                } as typeof this.data;
                if (preservedKind) {
                    this.contextCompanyKind = this.normalizePermissionCompanyKind(preservedKind);
                }
                
                this.updatePasswordValidation();
                
                // Limpar "mailto:" do email se presente ao carregar dados
                if (user.email && typeof user.email === 'string') {
                    user.email = user.email.replace(/^mailto:/i, '').trim();
                }
                
                // Para DOCTOR, remover campos técnicos do user antes de preencher o form
                // Esses campos serão carregados do doctor via loadDoctor
                if (this.config.type === 'DOCTOR') {
                    const userData: any = { ...user };
                    // Remover campos técnicos que vêm do doctor
                    delete userData.license_issue_date;
                    delete userData.license_expiry_date;
                    delete userData.license_issue_date_emission;
                    delete userData.graduation_institution;
                    delete userData.graduation_year;
                    delete userData.practice_area_or_courses;
                    delete userData.has_additional_courses;
                    delete userData.additional_courses_details;
                    delete userData.other_registration;
                    delete userData.telemedicine_experience;
                    delete userData.previous_aph_experience;
                    delete userData.shift_time_preferences;
                    delete userData.shift_day_preferences;
                    delete userData.shift_duration_preferences;
                    delete userData.average_hourly_rate;
                    delete userData.issuer_uf;
                    delete userData.registration;
                    delete userData.license_status;
                    delete userData.specialties_or_courses;
                    delete userData.notificationPreferences;
                    this.form.patchValue(userData);
                    this.mergeStoredNotificationPreferences(user.notificationPreferences);
                } else {
                    const userPatch: Partial<UserModel> & Record<string, unknown> = { ...user };
                    delete userPatch.notificationPreferences;
                    this.form.patchValue(userPatch as UserModel);
                    this.mergeStoredNotificationPreferences(user.notificationPreferences);
                }

                this.applyDocumentFieldAccess(user);

                // Se o usuário já tem senha, preencher com sentinela para indicar visualmente
                if (user.has_password) {
                    this.form.controls['password'].setValue(this.PASSWORD_SENTINEL);
                }

                // Carregar empresas vinculadas após carregar dados do usuário
                if (['DOCTOR', 'NURSE', 'NURSING_TECH', 'DRIVER', 'PARAMEDIC', 'COMPANY_ADMIN'].includes(this.config.type)) {
                    this.loadUserCompanies();
                }

                // Reaplicar filtro de grupos com o company_kind do contexto (após patch do user)
                this.getPermissionGroups();

                // Carregar dados do médico se for DOCTOR (isso sobrescreverá os campos técnicos)
                if (this.config.type === 'DOCTOR' && user.user_id) {
                    this.loadDoctor(user.user_id);
                }
            });
        // Load existing URLs for display
    }

    loadDoctor(userId: number): void {
        this.loadingDoctor = true;
        this.doctorService
            .getDoctor(userId)
            .pipe(
                take(1),
                finalize(() => (this.loadingDoctor = false)),
            )
            .subscribe({
                next: (doctor) => {
                    if (doctor) {
                        this.doctor = doctor;
                        // Preencher campos de doctor no form (dados técnicos vêm do doctor, não do user)
                        this.form.patchValue({
                            graduation_institution: doctor.graduation_institution,
                            graduation_year: doctor.graduation_year,
                            practice_area_or_courses: doctor.practice_area_or_courses,
                            has_additional_courses: doctor.has_additional_courses,
                            additional_courses_details: doctor.additional_courses_details,
                            other_registration: doctor.other_registration,
                            telemedicine_experience: doctor.telemedicine_experience,
                            previous_aph_experience: doctor.previous_aph_experience,
                            shift_time_preferences: doctor.shift_time_preferences,
                            shift_day_preferences: doctor.shift_day_preferences,
                            shift_duration_preferences: doctor.shift_duration_preferences,
                            average_hourly_rate: doctor.average_hourly_rate,
                            // Campos técnicos de datas vêm do doctor (não do user)
                            license_issue_date: doctor.license_issue_date,
                            license_expiry_date: doctor.license_expiry_date,
                            license_issue_date_emission: doctor.license_issue_date_emission,
                        });
                        // Carregar CRMs do médico
                        if (doctor.doctor_id) {
                            this.loadDoctorCrms(doctor.doctor_id);
                        }
                    }
                },
                error: (error) => {
                    console.error('Erro ao carregar dados do médico:', error);
                    // Se não encontrar doctor, pode ser que ainda não foi criado (usuário novo)
                },
            });
    }

    loadDoctorCrms(doctorId: number): void {
        this.loadingCrms = true;
        this.doctorCrmService
            .getCrms(doctorId)
            .pipe(
                take(1),
                finalize(() => (this.loadingCrms = false)),
            )
            .subscribe({
                next: (crms) => {
                    this.doctorCrms = crms || [];
                    // Ordenar por createdAt (primeiro é o principal)
                    this.doctorCrms.sort((a, b) => {
                        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                        return dateA - dateB;
                    });
                    // Atualizar dataSource
                    this.crmsDataSource.data = this.doctorCrms;
                        // Atualizar dataSource
                        this.crmsDataSource.data = this.doctorCrms;
                        // Se houver CRM principal (primeiro), preencher campos no form para compatibilidade
                        if (this.doctorCrms.length > 0) {
                            const primaryCrm = this.doctorCrms[0];
                            this.form.patchValue({
                                professional_number: primaryCrm.registration || `${primaryCrm.crm_number}-${primaryCrm.uf}`,
                                issuer_uf: primaryCrm.uf,
                                registration: primaryCrm.registration,
                                license_status: primaryCrm.status,
                                specialties_or_courses: primaryCrm.specialties,
                                license_issue_date: primaryCrm.license_issue_date,
                                license_expiry_date: primaryCrm.license_expiry_date,
                                license_issue_date_emission: primaryCrm.license_issue_date_emission,
                            });
                        }
                },
                error: (error) => {
                    console.error('Erro ao carregar CRMs do médico:', error);
                },
            });
    }

    openCrmDialog(crm?: DoctorCrmModel): void {
        // Se não houver doctor carregado, tentar carregar primeiro
        if (!this.doctor?.doctor_id) {
            if (!this.data?.user_id) {
                alert('Erro: Usuário não foi criado ainda. Por favor, salve o usuário primeiro.');
                return;
            }

            // Tentar carregar o doctor
            this.loadingDoctor = true;
            this.doctorService
                .getDoctor(this.data.user_id)
                .pipe(
                    take(1),
                    finalize(() => (this.loadingDoctor = false)),
                )
                .subscribe({
                    next: (doctor) => {
                        if (doctor && doctor.doctor_id) {
                            this.doctor = doctor;
                            this.openCrmDialogInternal(crm);
                        } else {
                            // Doctor não existe, criar agora
                            this.createDoctorAndOpenDialog(crm);
                        }
                    },
                    error: (error) => {
                        console.error('Erro ao carregar doctor:', error);
                        // Doctor não existe, tentar criar
                        this.createDoctorAndOpenDialog(crm);
                    },
                });
            return;
        }

        this.openCrmDialogInternal(crm);
    }

    private createDoctorAndOpenDialog(crm?: DoctorCrmModel): void {
        if (!this.data?.user_id) {
            alert('Erro: Usuário não foi criado ainda. Por favor, salve o usuário primeiro.');
            return;
        }

        // Criar doctor vazio primeiro
        const doctorData: Partial<DoctorModel> = {
            user_id: this.data.user_id,
            graduation_institution: this.form.get('graduation_institution')?.value,
            graduation_year: this.form.get('graduation_year')?.value,
            practice_area_or_courses: this.form.get('practice_area_or_courses')?.value,
            has_additional_courses: this.form.get('has_additional_courses')?.value,
            additional_courses_details: this.form.get('additional_courses_details')?.value,
            other_registration: this.form.get('other_registration')?.value,
            telemedicine_experience: this.form.get('telemedicine_experience')?.value,
            previous_aph_experience: this.form.get('previous_aph_experience')?.value,
            shift_time_preferences: this.form.get('shift_time_preferences')?.value,
            shift_day_preferences: this.form.get('shift_day_preferences')?.value,
            shift_duration_preferences: this.form.get('shift_duration_preferences')?.value,
            average_hourly_rate: this.form.get('average_hourly_rate')?.value,
        };

        this.loadingDoctor = true;
        this.doctorService
            .create(doctorData)
            .pipe(
                take(1),
                finalize(() => (this.loadingDoctor = false)),
            )
            .subscribe({
                next: (doctor) => {
                    this.doctor = doctor;
                    this.openCrmDialogInternal(crm);
                },
                error: (error) => {
                    console.error('Erro ao criar doctor:', error);
                    alert(`Erro ao criar registro do médico: ${error.error?.message || error.message || 'Erro desconhecido'}`);
                },
            });
    }

    private openCrmDialogInternal(crm?: DoctorCrmModel): void {
        if (!this.doctor?.doctor_id) {
            alert('Erro: Não foi possível obter o ID do médico. Por favor, tente novamente.');
            return;
        }

        const dialogRef = this.dialog.open(DoctorCrmDialogComponent, {
            width: '600px',
            data: {
                doctor_id: this.doctor.doctor_id,
                doctor_crm: crm || undefined,
            },
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result && this.doctor?.doctor_id) {
                // Recarregar CRMs após criar/editar
                this.loadDoctorCrms(this.doctor.doctor_id);
            }
        });
    }

    getPrimaryCrm(): DoctorCrmModel | null {
        return this.doctorCrms.length > 0 ? this.doctorCrms[0] : null;
    }

    calcAge(): void {
        const birthday = this.form.get('birthday_date')?.value;
        if (birthday) {
            const birthDate = new Date(birthday);
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--;
            }
            this.form.controls['age'].setValue(age, { emitEvent: false });
        } else {
            this.form.controls['age'].setValue(null, { emitEvent: false });
        }
    }

    onBirthdayDateChange(): void {
        this.form.controls['birthday_date'].valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((value) => {
            // Input editável: string vazia ao apagar → null
            if (value === '' || value === undefined) {
                this.form.controls['birthday_date'].setValue(null, { emitEvent: false });
            }
            this.calcAge();
        });
    }

    fetchCep() {
        const cep = this.form.controls['cep'].value;
        if (cep && cep.length === 8) {
            this.cepService
                .buscar(cep)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe((endereco) => {
                    this.form.patchValue(
                        {
                            address: endereco.street,
                            city: endereco.city,
                            cep: endereco.cep,
                        },
                        {
                            emitEvent: false,
                        },
                    );
                });
        }
    }
    loadUserCompanies(): void {
        const user_id = this.form.get('user_id')?.value;
        if (!user_id) {
            // No CREATE: verificar se há company_id selecionado
            const company_id = this.form.get('company_id')?.value;
            if (company_id) {
                // Buscar empresa e adicionar na lista como temporária
                this.loadingCompanies = true;
                this.companyService
                    .getById(company_id)
                    .pipe(
                        take(1),
                        finalize(() => (this.loadingCompanies = false)),
                    )
                    .subscribe({
                        next: (company) => {
                            const tempUserCompany = new UserCompanyModel({
                                user_company_id: null,
                                user_id: null,
                                company_id: company_id,
                                company: company,
                                createdAt: null,
                            });
                            this.companiesDataSource.data = [tempUserCompany];
                        },
                        error: () => {
                            this.companiesDataSource.data = [];
                        },
                    });
            } else {
                this.companiesDataSource.data = [];
            }
            return;
        }

        // Comportamento atual para UPDATE
        this.loadingCompanies = true;
        this.userCompanyService
            .getByUser(user_id)
            .pipe(
                take(1),
                finalize(() => (this.loadingCompanies = false)),
            )
            .subscribe({
                next: (data) => {
                    this.userCompanies = data || [];
                    this.companiesDataSource.data = this.userCompanies;
                },
                error: (err) => {
                    console.error('Erro ao carregar empresas vinculadas:', err);
                    this.userCompanies = [];
                    this.companiesDataSource.data = [];
                },
            });
    }

    addCompany(): void {
        const user_id = this.form.get('user_id')?.value;
        const isEditMode = !!user_id;

        const isAdmin = this.currentUser?.is_admin || false;
        const currentCompany = this.companyService.getCurrentCompany();

        if (isAdmin) {
            // Se for admin, abrir modal de seleção de empresa
            // Pré-selecionar empresa atual no filtro se existir
            const displayColumns = [
                { name: 'Nome', column: 'name' },
                { name: 'CNPJ', column: 'num_doc', mask: '00.000.000/0000-00' },
            ];

            const filtro: IFilterPagination<CompanyModel> = {
                page: 1,
                limit: 10,
                company_kind: 'client',
            };

            const dialogRef = this.dialog.open(PesquisaDialogComponent, {
                width: '800px',
                data: {
                    title: 'Selecionar Empresa',
                    service: this.companyService,
                    displayColumns: displayColumns,
                    filtro: filtro,
                },
            });

            dialogRef.afterClosed().subscribe((result: CompanyModel) => {
                if (!result) return;

                const company_id = result.company_id;
                if (company_id) {
                    if (isEditMode) {
                        // No UPDATE: associar quando fecha o modal (comportamento atual)
                        this.associateUserCompany(user_id, company_id);
                    } else {
                        // No CREATE: atualizar company_id no formulário para enviar junto no POST
                        this.form.get('company_id')?.setValue(company_id);
                        
                        // Adicionar empresa na lista para exibição (CREATE)
                        const existingIndex = this.companiesDataSource.data.findIndex(
                            (uc) => uc.company_id === company_id && !uc.user_company_id
                        );
                        if (existingIndex === -1) {
                            const tempUserCompany = new UserCompanyModel({
                                user_company_id: null,
                                user_id: null,
                                company_id: company_id,
                                company: result,
                                createdAt: null,
                            });
                            this.companiesDataSource.data = [...this.companiesDataSource.data, tempUserCompany];
                        }
                    }
                }
            });
        } else {
            // Se não for admin, usar empresa atual
            if (!currentCompany?.company_id) {
                console.error('Empresa atual não encontrada');
                return;
            }

            if (isEditMode) {
                // No UPDATE: associar quando fecha o modal (comportamento atual)
                this.associateUserCompany(user_id, currentCompany.company_id);
            } else {
                // No CREATE: atualizar company_id no formulário para enviar junto no POST
                this.form.get('company_id')?.setValue(currentCompany.company_id);
                
                // Adicionar empresa na lista para exibição (CREATE)
                const existingIndex = this.companiesDataSource.data.findIndex(
                    (uc) => uc.company_id === currentCompany.company_id && !uc.user_company_id
                );
                if (existingIndex === -1) {
                    const tempUserCompany = new UserCompanyModel({
                        user_company_id: null,
                        user_id: null,
                        company_id: currentCompany.company_id,
                        company: currentCompany,
                        createdAt: null,
                    });
                    this.companiesDataSource.data = [...this.companiesDataSource.data, tempUserCompany];
                }
            }
        }
    }

    private associateUserCompany(user_id: number, company_id: number): void {
        this.loadingCompanies = true;
        this.userCompanyService
            .associate(user_id, company_id)
            .pipe(
                take(1),
                finalize(() => (this.loadingCompanies = false)),
            )
            .subscribe({
                next: () => {
                    this.loadUserCompanies();
                },
                error: (err) => {
                    console.error('Erro ao associar empresa:', err);
                },
            });
    }

    removeCompany(userCompany: UserCompanyModel): void {
        // Se for empresa temporária (CREATE), apenas remover da lista
        if (!userCompany.user_company_id || !userCompany.user_id) {
            // Remover da lista
            this.companiesDataSource.data = this.companiesDataSource.data.filter(
                (uc) => !(uc.company_id === userCompany.company_id && !uc.user_company_id)
            );
            // Limpar company_id se for a empresa selecionada
            const currentCompanyId = this.form.get('company_id')?.value;
            if (currentCompanyId === userCompany.company_id) {
                this.form.get('company_id')?.setValue(null);
            }
            return;
        }

        // Comportamento atual para empresas associadas (UPDATE)
        if (!userCompany.user_id || !userCompany.company_id) {
            return;
        }

        const dialogRef = this._devstroupeConfirmationService.open({
            title: 'Remover Vinculação',
            message: 'Deseja realmente remover a vinculação com esta empresa?',
            actions: {
                confirm: {
                    show: true,
                    label: 'Remover',
                    color: 'warn',
                },
                cancel: {
                    show: true,
                    label: 'Cancelar',
                },
            },
        });

        dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                if (result === 'confirmed') {
                    this.loadingCompanies = true;
                    this.userCompanyService
                        .disassociate(userCompany.user_id, userCompany.company_id)
                        .pipe(
                            take(1),
                            finalize(() => (this.loadingCompanies = false)),
                        )
                        .subscribe({
                            next: () => {
                                this.loadUserCompanies();
                            },
                            error: (err) => {
                                console.error('Erro ao remover vinculação:', err);
                            },
                        });
                }
            });
    }

    onCepChange() {
        this.form.controls['cep'].valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
            this.fetchCep();
        });
    }

    onPhotoSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length) {
            const file = input.files[0];
            const formData = new FormData();
            formData.append('file', file, file.name);

            this.service.uploadPhoto(formData).subscribe({
                next: (res: any) => {
                    this.form.patchValue({ photo: res.url });
                },
                error: () => alert('Erro ao enviar foto!'),
            });
        }
    }

    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length) {
            const file = input.files[0];
            const formData = new FormData();
            formData.append('file', file, file.name);

            this.service.uploadFile(formData).subscribe({
                next: (res: any) => {
                    this.form.patchValue({ document_card: res.url });
                },
                error: () => alert('Erro ao enviar documento!'),
            });
        }
    }

    consultMedic() {
        const professionalNumber = this.form.controls['professional_number'].value;
        
        // Extrair CRM e UF do formato "12345-GO"
        const crmRegex = /^(\d+)-([A-Z]{2})$/;
        const match = professionalNumber?.match(crmRegex);
        
        if (!match) {
            alert('CRM deve estar no formato: NÚMERO-UF (ex: 12345-GO)');
            return;
        }
        
        const [, crm, uf] = match;
        
        const data = {
            crm: parseInt(crm, 10),
            uf: uf,
            cpf: this.form.controls['type_doc'].value === 'CPF' ? this.form.controls['num_doc'].value : null,
        };
        
        this.cfmService.consult(data).subscribe((res) => {
            // Manter o formato completo no professional_number
            this.form.controls['professional_number'].setValue(`${res.crm}-${res.uf}`);
            this.form.controls['issuer_uf'].setValue(res.uf);
            this.form.controls['license_status'].setValue(res.situacao);
            this.form.controls['specialties_or_courses'].setValue(res.especialidade);
            
            // Garantir que registration também está no formato correto
            this.form.controls['registration'].setValue(`${res.crm}-${res.uf}`);
        });
    }

    onCrmBlur(): void {
        if (this.config.type === 'DOCTOR') {
            const value = this.form.controls['professional_number'].value;
            if (value && typeof value === 'string') {
                // Remover espaços e converter para maiúsculas
                const cleaned = value.trim().toUpperCase();
                // Se não tiver hífen, tentar adicionar baseado no issuer_uf
                if (!cleaned.includes('-') && this.form.controls['issuer_uf'].value) {
                    const uf = this.form.controls['issuer_uf'].value.toUpperCase();
                    this.form.controls['professional_number'].setValue(`${cleaned}-${uf}`);
                } else {
                    this.form.controls['professional_number'].setValue(cleaned);
                }
            }
        }
    }

    consultMedicAutomatically(): void {
        if (this.config.type !== 'DOCTOR') {
            return;
        }

        const professionalNumber = this.form.controls['professional_number'].value;
        
        if (!professionalNumber) {
            return;
        }
        
        const crmRegex = /^(\d+)-([A-Z]{2})$/;
        const match = professionalNumber.match(crmRegex);
        
        if (!match) {
            return;
        }
        
        const [, crm, uf] = match;
        
        const data = {
            crm: parseInt(crm, 10),
            uf: uf,
            cpf: this.form.controls['type_doc'].value === 'CPF' ? this.form.controls['num_doc'].value : null,
        };
        
        this.cfmService.consult(data).subscribe({
            next: (res) => {
                // Preencher campos automaticamente (sem emitir eventos para evitar loops)
                this.form.controls['professional_number'].setValue(`${res.crm}-${res.uf}`, { emitEvent: false });
                this.form.controls['issuer_uf'].setValue(res.uf, { emitEvent: false });
                this.form.controls['license_status'].setValue(res.situacao, { emitEvent: false });
                this.form.controls['specialties_or_courses'].setValue(res.especialidade, { emitEvent: false });
                this.form.controls['registration'].setValue(`${res.crm}-${res.uf}`, { emitEvent: false });
            },
            error: (error) => {
                // Não bloquear, apenas logar erro (opcional: mostrar mensagem)
                console.warn('Erro ao consultar CFM automaticamente:', error);
            }
        });
    }

    checkDoctorStatusCFM(): void {
        const userId = this.form.controls['user_id'].value;
        const registration = this.form.controls['registration'].value;

        if (!userId) {
            alert('Por favor, salve o médico antes de verificar o status no CFM.');
            return;
        }

        if (!registration || registration.trim() === '') {
            alert('O médico precisa ter um CRM cadastrado (campo Inscrição) para verificar o status no CFM.');
            return;
        }

        if (this.config.type !== 'DOCTOR') {
            return;
        }

        this.loading = true;
        this.cfmService.checkDoctorStatus(userId).subscribe({
            next: (result) => {
                this.loading = false;

                // Atualizar campos do formulário com os dados retornados
                if (result.statusUpdated) {
                    this.form.controls['status'].setValue(result.isActive);
                    this.form.controls['license_status'].setValue(result.cfmStatus);
                }

                // Exibir mensagem de sucesso
                const statusMessage = result.isActive ? 'ATIVO' : result.cfmStatus;
                if (result.statusUpdated) {
                    alert(`Status do médico atualizado com sucesso! Status atual: ${statusMessage}`);
                } else {
                    alert(`Status do médico verificado! Status atual: ${statusMessage}`);
                }
            },
            error: (error) => {
                this.loading = false;
                const errorMessage = error?.error?.message || error?.message || 'Erro ao verificar status do médico no CFM.';
                alert(`Erro: ${errorMessage}`);
            },
        });
    }

    get canCheckDoctorStatus(): boolean {
        return (
            this.config.type === 'DOCTOR' &&
            this.form.controls['user_id'].value &&
            this.form.controls['registration'].value &&
            this.form.controls['registration'].value.trim() !== ''
        );
    }

    getStatusBadgeClass(status: string): string {
        switch (status?.toUpperCase()) {
            case 'ATIVO':
                return 'bg-green-100 text-green-800';
            case 'INATIVO':
                return 'bg-gray-100 text-gray-800';
            case 'SUSPENSO':
                return 'bg-yellow-100 text-yellow-800';
            case 'CANCELADO':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    }

    /** Rótulo amigável do status da licença (mesmas chaves que as opções do select) para i18n. */
    getLicenseStatusLabel(status: string | null | undefined): string {
        switch (status?.toUpperCase()) {
            case 'ATIVO':
                return 'Ativo';
            case 'INATIVO':
                return 'Inativo';
            case 'SUSPENSO':
                return 'Suspenso';
            case 'CANCELADO':
                return 'Cancelado';
            default:
                return status || '-';
        }
    }

    onShiftTimeChange(event: any, value: string): void {
        const currentValues = this.form.get('shift_time_preferences')?.value || [];
        if (event.checked) {
            if (!currentValues.includes(value)) {
                this.form.patchValue({
                    shift_time_preferences: [...currentValues, value],
                });
            }
        } else {
            this.form.patchValue({
                shift_time_preferences: currentValues.filter((v: string) => v !== value),
            });
        }
    }

    onShiftDayChange(event: any, value: string): void {
        const currentValues = this.form.get('shift_day_preferences')?.value || [];
        if (event.checked) {
            if (!currentValues.includes(value)) {
                this.form.patchValue({
                    shift_day_preferences: [...currentValues, value],
                });
            }
        } else {
            this.form.patchValue({
                shift_day_preferences: currentValues.filter((v: string) => v !== value),
            });
        }
    }

    onShiftDurationChange(event: any, value: string): void {
        const currentValues = this.form.get('shift_duration_preferences')?.value || [];
        if (event.checked) {
            if (!currentValues.includes(value)) {
                this.form.patchValue({
                    shift_duration_preferences: [...currentValues, value],
                });
            }
        } else {
            this.form.patchValue({
                shift_duration_preferences: currentValues.filter((v: string) => v !== value),
            });
        }
    }
}
